// src/utils/clientFormUtils.js
// Utilitarios para formularios de cliente (ADD e EDIT)

/**
 * Estado inicial do formulario de cliente
 */
export const INITIAL_FORM_STATE = {
  name: '',
  address: '',
  hours: '',
  email: '',
  phone: '',
  defaultAmount: '',
  paymentMethod: 'cash',
  password: '',
  checklist: [],
  notes: '',
  restrictedTimes: {
    enabled: false,
    allowedStart: '',
    allowedEnd: ''
  }
};

/**
 * Valida dados do formulario de cliente
 * @param {Object} formData - Dados do formulario
 * @param {string} language - Idioma para mensagens ('pt' ou 'en')
 * @returns {Object} { isValid: boolean, error: string | null }
 */
export const validateClientForm = (formData, language = 'pt') => {
  // Nome obrigatorio
  if (!formData.name?.trim()) {
    return {
      isValid: false,
      error: language === 'pt'
        ? 'Nome do cliente é obrigatório'
        : 'Client name is required'
    };
  }

  // Endereco obrigatorio
  if (!formData.address?.trim()) {
    return {
      isValid: false,
      error: language === 'pt'
        ? 'Endereço é obrigatório'
        : 'Address is required'
    };
  }

  // Horas no formato HH:MM
  if (!formData.hours || !/^\d{1,2}:\d{2}$/.test(formData.hours)) {
    return {
      isValid: false,
      error: language === 'pt'
        ? 'Tempo deve estar no formato HH:MM (ex: 2:30)'
        : 'Time must be in HH:MM format (e.g., 2:30)'
    };
  }

  // Email obrigatorio se metodo de pagamento for Xero
  if (formData.paymentMethod === 'xero' && !formData.email?.trim()) {
    return {
      isValid: false,
      error: language === 'pt'
        ? 'Email é obrigatório para clientes com pagamento via Xero'
        : 'Email is required for Xero payment clients'
    };
  }

  return { isValid: true, error: null };
};

/**
 * Converte cliente do banco para formato do formulario
 * Trata fallbacks PostgreSQL <-> Firestore
 * @param {Object} client - Cliente do banco
 * @returns {Object} Dados no formato do formulario
 */
export const mapClientToFormData = (client) => {
  if (!client) return { ...INITIAL_FORM_STATE };

  // Parse checklist (JSONB no PostgreSQL)
  let checklist = [];
  try {
    const rawChecklist = client.default_checklist || client.checklist || [];
    checklist = typeof rawChecklist === 'string'
      ? JSON.parse(rawChecklist)
      : rawChecklist;
  } catch (e) {
    console.warn('Erro ao fazer parse do checklist:', e);
    checklist = [];
  }

  // Parse restricted times (JSONB no PostgreSQL)
  let restrictedTimes = { enabled: false, allowedStart: '', allowedEnd: '' };
  try {
    const rawRestricted = client.restricted_times || client.restrictedTimes;
    if (rawRestricted) {
      const parsed = typeof rawRestricted === 'string'
        ? JSON.parse(rawRestricted)
        : rawRestricted;
      restrictedTimes = {
        enabled: parsed.enabled || false,
        allowedStart: parsed.allowedStart || '',
        allowedEnd: parsed.allowedEnd || ''
      };
    }
  } catch (e) {
    console.warn('Erro ao fazer parse de restricted_times:', e);
  }

  return {
    name: client.name || '',
    address: client.address || '',
    hours: client.service_duration || client.hours || '',
    email: client.email || '',
    phone: client.phone || '',
    defaultAmount: String(client.default_price || client.defaultInvoiceAmount || ''),
    paymentMethod: client.payment_method || client.paymentMethod || 'cash',
    password: client.password || '',
    checklist,
    notes: client.notes || '',
    restrictedTimes
  };
};

/**
 * Converte dados do formulario para formato da API (snake_case)
 * @param {Object} formData - Dados do formulario
 * @returns {Object} Dados no formato da API
 */
export const mapFormDataToClient = (formData) => {
  const normalizedAddress = normalizeAddress(formData.address?.trim() || '');

  return {
    name: formData.name?.trim() || '',
    address: normalizedAddress,
    service_duration: formData.hours || '',
    email: formData.email?.trim() || '',
    phone: formData.phone?.trim() || '',
    default_price: formData.defaultAmount ? parseFloat(formData.defaultAmount) : 0,
    payment_method: formData.paymentMethod || 'cash',
    password: formData.password?.trim() || '',
    default_checklist: JSON.stringify(formData.checklist || []),
    notes: formData.notes?.trim() || '',
    restricted_times: JSON.stringify(formData.restrictedTimes || {
      enabled: false,
      allowedStart: '',
      allowedEnd: ''
    })
  };
};

/**
 * Normaliza endereco para formato padrao (Auckland, New Zealand)
 * @param {string} address - Endereco bruto
 * @returns {string} Endereco normalizado
 */
export const normalizeAddress = (address) => {
  if (!address) return '';
  let addr = address.trim();

  // Remove "New Zealand" e "Nova Zelândia"
  addr = addr.replace(/,?\s*(New Zealand|Nova Zelândia)\s*$/i, '');
  // Remove código postal sozinho
  addr = addr.replace(/,?\s*Auckland\s+\d{4}\s*$/i, ', Auckland');

  // Garante que termina com ", Auckland, New Zealand"
  if (!addr.includes('Auckland')) {
    const parts = addr.split(',').map(p => p.trim());
    if (parts.length >= 2) {
      addr = `${parts[0]}, ${parts[1]}`;
    }
    addr = addr + ', Auckland';
  } else {
    addr = addr.replace(/,?\s*Auckland\s*$/i, ', Auckland');
  }

  if (!addr.includes('New Zealand')) {
    addr = addr + ', New Zealand';
  }

  return addr.trim();
};

/**
 * Detecta mudancas entre cliente original e dados do formulario
 * @param {Object} originalClient - Cliente original do banco
 * @param {Object} formData - Dados atuais do formulario
 * @returns {Object} Objeto com campos alterados { campo: "valor antigo → valor novo" }
 */
export const detectClientChanges = (originalClient, formData) => {
  const changes = {};

  // Mapeia cliente original para formato comparavel
  const original = mapClientToFormData(originalClient);

  // Campos simples para comparar
  const fieldsToCompare = [
    { key: 'name', label: 'Nome' },
    { key: 'address', label: 'Endereço' },
    { key: 'hours', label: 'Tempo' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Telefone' },
    { key: 'defaultAmount', label: 'Valor Padrão' },
    { key: 'paymentMethod', label: 'Método Pagamento' },
    { key: 'password', label: 'Senha' },
    { key: 'notes', label: 'Notas' }
  ];

  fieldsToCompare.forEach(({ key, label }) => {
    const oldVal = String(original[key] || '');
    const newVal = String(formData[key] || '');

    if (oldVal !== newVal) {
      let changeText = `"${oldVal}" → "${newVal}"`;

      // Flag especial para aumento de horas
      if (key === 'hours' && oldVal && newVal) {
        const [oldH, oldM] = oldVal.split(':').map(Number);
        const [newH, newM] = newVal.split(':').map(Number);
        const oldMinutes = (oldH || 0) * 60 + (oldM || 0);
        const newMinutes = (newH || 0) * 60 + (newM || 0);
        if (newMinutes > oldMinutes) {
          changeText += ' (AUMENTO)';
        }
      }

      changes[label] = changeText;
    }
  });

  // Compara restricted times
  const oldRestricted = JSON.stringify(original.restrictedTimes);
  const newRestricted = JSON.stringify(formData.restrictedTimes);
  if (oldRestricted !== newRestricted) {
    changes['Horários Restritos'] = `Alterado`;
  }

  // Compara checklist
  const oldChecklist = JSON.stringify(original.checklist);
  const newChecklist = JSON.stringify(formData.checklist);
  if (oldChecklist !== newChecklist) {
    changes['Checklist'] = `Alterado`;
  }

  return changes;
};

/**
 * Formata mudancas para exibicao em log/webhook
 * @param {Object} changes - Objeto de mudancas do detectClientChanges
 * @returns {string} String formatada para log
 */
export const formatChangesForLog = (changes) => {
  return Object.entries(changes)
    .map(([key, value]) => `${key}: ${value}`)
    .join(' | ');
};
